// Frontend-only database layer that makes HTTP requests to MySQL API
// This file replaces the direct MySQL imports for the frontend

// Re-export all types from mysql-db for compatibility
export type {
  NewsItem,
  SlideItem,
  GalleryItem,
  FontFile,
  Font,
  SiteSettings,
  CustomSection,
  AboutData,
  ImprintData,
  PrivacyData,
  ContactInfo,
  PosterItem,
  PosterImage,
  BieberInfoSettings,
  WiesenInfoSettings,
  VereineSettings,
  ContactFormSettings,
  ContactFormRequest
} from './mysql-db';

import type { NewsItem } from './mysql-db';

class FrontendDatabase {
  private baseUrl = '/api';

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Database request failed:', error);
      throw error;
    }
  }

  // Time methods
  async getServerTime(): Promise<{ isoUtc: string; berlinDate: string; berlinIso: string }> {
    return this.request<{ isoUtc: string; berlinDate: string; berlinIso: string }>('/time/now');
  }

  // News methods
  async getNews(): Promise<NewsItem[]> {
    return this.request<NewsItem[]>('/news');
  }

  async createNews(data: { title: string; content: string; image_url: string; created_at?: string }) {
    return this.request('/news', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNews(id: string, data: Partial<{ title: string; content: string; image_url: string; created_at: string }>) {
    return this.request(`/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteNews(id: string) {
    return this.request(`/news/${id}`, {
      method: 'DELETE',
    });
  }

  // Slideshow methods
  async getSlides() {
    return this.request('/slides');
  }

  async createSlide(data: any) {
    return this.request('/slides', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSlide(id: string, data: any) {
    return this.request(`/slides/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSlide(id: string) {
    return this.request(`/slides/${id}`, {
      method: 'DELETE',
    });
  }

  async updateSlidesOrder(slides: any[]) {
    return this.request('/slides/order', {
      method: 'PUT',
      body: JSON.stringify({ slides }),
    });
  }

  // Gallery methods
  async getGallery() {
    return this.request('/gallery');
  }

  async createGalleryItem(data: any) {
    return this.request('/gallery', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGalleryItem(id: string, data: any) {
    return this.request(`/gallery/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteGalleryItem(id: string) {
    return this.request(`/gallery/${id}`, {
      method: 'DELETE',
    });
  }

  // Settings methods
  async getSettings() {
    return this.request('/settings');
  }

  async updateSettings(data: any) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Custom sections methods
  async getCustomSections() {
    return this.request('/custom-sections');
  }

  async createCustomSection(data: any) {
    return this.request('/custom-sections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomSection(id: string, data: any) {
    return this.request(`/custom-sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomSection(id: string) {
    return this.request(`/custom-sections/${id}`, {
      method: 'DELETE',
    });
  }

  async updateCustomSectionsOrder(sections: any[]) {
    return this.request('/custom-sections/order', {
      method: 'PUT',
      body: JSON.stringify({ sections }),
    });
  }

  // About methods
  async getAbout() {
    return this.request('/about');
  }

  async updateAbout(content: string, section_title?: string) {
    return this.request('/about', {
      method: 'PUT',
      body: JSON.stringify({ content, section_title }),
    });
  }

  // Imprint methods
  async getImprint() {
    return this.request('/imprint');
  }

  async updateImprint(content: string) {
    return this.request('/imprint', {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  // Privacy methods
  async getPrivacy() {
    return this.request('/privacy');
  }

  async updatePrivacy(content: string) {
    return this.request('/privacy', {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  // Contact methods
  async getContact() {
    return this.request('/contact');
  }

  async updateContact(data: any) {
    return this.request('/contact', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Poster methods
  async getPosters() {
    return this.request('/posters');
  }

  async getPoster(id: string) {
    return this.request(`/posters/${id}`);
  }

  async getPosterBySlug(slug: string) {
    return this.request(`/posters/slug/${encodeURIComponent(slug)}`);
  }

  async createPoster(data: any) {
    return this.request('/posters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePoster(id: string, data: any) {
    return this.request(`/posters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePoster(id: string) {
    return this.request(`/posters/${id}`, {
      method: 'DELETE',
    });
  }

  // Poster images methods
  async getPosterImages(posterId: string) {
    return this.request(`/posters/${posterId}/images`);
  }

  async createPosterImage(data: any) {
    return this.request('/poster-images', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePosterImage(id: string, data: any) {
    return this.request(`/poster-images/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePosterImage(id: string) {
    return this.request(`/poster-images/${id}`, {
      method: 'DELETE',
    });
  }

  // Font methods
  async getFonts() {
    return this.request('/fonts');
  }

  async uploadFont(file: File) {
    try {
      const formData = new FormData();
      formData.append('font', file);

      const response = await fetch('/api/fonts/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error uploading font:', error);
      throw error;
    }
  }

  async deleteFont(id: string) {
    return this.request(`/fonts/${id}`, {
      method: 'DELETE',
    });
  }

  // Info page methods
  async getBieberInfo() {
    return this.request('/bieber-info');
  }

  async updateBieberInfo(data: any) {
    return this.request('/bieber-info', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getWiesenInfo() {
    return this.request('/wiesen-info');
  }

  async updateWiesenInfo(data: any) {
    return this.request('/wiesen-info', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getVereineSettings() {
    return this.request('/vereine-settings');
  }

  async updateVereineSettings(data: any) {
    return this.request('/vereine-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Image storage method
  async storeImage(file: File, posterSlug?: string) {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (posterSlug) {
        formData.append('posterSlug', posterSlug);
      }

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      // Server gibt { url } zurück
      return result.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  // Contact Form methods
  async getContactFormSettings() {
    return this.request('/contact-form-settings');
  }

  async updateContactFormSettings(data: any) {
    return this.request('/contact-form-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getContactFormRequests() {
    return this.request('/contact-form-requests');
  }

  async submitContactForm(data: { name: string; email: string; subject: string; message: string }) {
    return this.request('/contact-form-submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const frontendDb = new FrontendDatabase();
export default frontendDb;